import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { AppStateService } from '../../core/service/app-state.service';
import { MaintenanceScreenComponent } from './maintenance-screen.component';
import { ForceUpdateScreenComponent } from './force-update-screen.component';
import { SystemMessageBannerComponent } from './system-message-banner.component';

@Component({
  selector: 'app-state-wrapper',
  standalone: true,
  imports: [
    ToastModule,
    ButtonModule,
    MaintenanceScreenComponent,
    ForceUpdateScreenComponent,
    SystemMessageBannerComponent,
  ],
  styles: [`
    :host ::ng-deep .p-toast-message-content {
      position: relative;
      width: 100%;
    }

    :host ::ng-deep .p-toast-icon-close {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
    }

    .toast-update-content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
      padding-right: 1.5rem;
    }

    .toast-update-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .toast-update-detail {
      font-size: 0.875rem;
    }

    .toast-update-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 0.25rem;
    }
  `],
  template: `
    <p-toast key="app-state">
      <ng-template let-message #message>
        <div class="toast-update-content">
          <div class="toast-update-header">
            <i class="pi pi-info-circle"></i>
            <strong>{{ message.summary }}</strong>
          </div>
          <span class="toast-update-detail">{{ message.detail }}</span>
          <div class="toast-update-actions">
            <p-button
              label="Rafraichir"
              icon="pi pi-refresh"
              size="small"
              (click)="onForceRefresh()"
              severity="info">
            </p-button>
          </div>
        </div>
      </ng-template>
    </p-toast>

    @if (showMaintenance()) {
      <app-maintenance-screen
        [message]="maintenanceMessage()"
        (retry)="onRetry()">
      </app-maintenance-screen>
    }

    @if (showForceUpdate()) {
      <app-force-update-screen
        [currentVersion]="forceUpdateCurrentVersion()"
        [minimumVersion]="forceUpdateMinimumVersion()"
        (retry)="onRetry()">
      </app-force-update-screen>
    }

    @if (showSystemMessage()) {
      <app-system-message-banner
        [systemMessage]="systemMessageData()!"
        (dismiss)="onDismissSystemMessage()">
      </app-system-message-banner>
    }
  `,
})
export class AppStateWrapperComponent implements OnInit {
  #appStateService = inject(AppStateService);
  #messageService = inject(MessageService);
  #optionalUpdateToastShown = false;

  showMaintenance = computed(() => {
    return this.#appStateService.checkResult().status === 'maintenance';
  });

  maintenanceMessage = computed(() => {
    const result = this.#appStateService.checkResult();
    return result.status === 'maintenance' ? result.message : '';
  });

  showForceUpdate = computed(() => {
    return this.#appStateService.checkResult().status === 'force-update';
  });

  forceUpdateCurrentVersion = computed(() => {
    const result = this.#appStateService.checkResult();
    return result.status === 'force-update' ? result.currentVersion : '';
  });

  forceUpdateMinimumVersion = computed(() => {
    const result = this.#appStateService.checkResult();
    return result.status === 'force-update' ? result.minimumVersion : '';
  });

  showSystemMessage = computed(() => {
    const result = this.#appStateService.checkResult();
    return result.status === 'ok' && result.systemMessage !== null;
  });

  systemMessageData = computed(() => {
    const result = this.#appStateService.checkResult();
    return result.status === 'ok' ? result.systemMessage : null;
  });

  constructor() {
    effect(() => {
      const result = this.#appStateService.checkResult();
      if (
        result.status === 'ok' &&
        result.optionalUpdate &&
        !this.#optionalUpdateToastShown
      ) {
        this.#optionalUpdateToastShown = true;
        this.#messageService.add({
          key: 'app-state',
          severity: 'info',
          summary: 'Mise a jour disponible',
          detail: `Une nouvelle version (${result.latestVersion}) est disponible.`,
          sticky: true,
        });
      }
    });
  }

  ngOnInit(): void {
    this.#appStateService.setupVisibilityChangeListener();
  }

  onRetry(): void {
    this.#appStateService.refreshAppState();
  }

  onDismissSystemMessage(): void {
    this.#appStateService.dismissSystemMessage();
  }

  onForceRefresh(): void {
    window.location.reload();
  }
}
